import { autobind } from "@typed-decorators/autobind";
import classNames from "classnames";
import {
  KEY_UP,
  KEY_DOWN,
  KEY_ESCAPE,
  KEY_RETURN,
  KEY_TAB,
  KEY_HOME,
  KEY_END,
  KEY_PAGE_UP,
  KEY_PAGE_DOWN,
  KEY_P,
  KEY_N,
} from "keycode-js";
import type { Root } from "react-dom/client";
import { createRoot } from "react-dom/client";

import type { RangeType } from "../../../vendor/prosemirror-suggestions";
import { suggestionsPlugin } from "../../../vendor/prosemirror-suggestions";
import type { AutoCompletion, CompletionItem } from "../../shared/editor/completions";

const PopupMaxHeight = 17; // 17em

type Props = Readonly<{
  items: ReadonlyArray<CompletionItem>;
  completion: AutoCompletion<CompletionItem>;
  selectedIndex: number;
  onClick: (item: any) => void;
}>;

const Hints: React.FC<Props> = ({ items, completion, selectedIndex, onClick }) => {
  return (
    <ul className="completion-plugin__completion-list">
      {items.map((item, idx) => (
        <li
          key={item.text}
          className={classNames("completion-plugin__completion-list-item", { "is-selected": idx === selectedIndex })}
          onClick={() => onClick(item)}
        >
          {completion.component(item)}
        </li>
      ))}
    </ul>
  );
};

class HintsAdapter<ItemType extends CompletionItem> {
  private state: {
    index: number;
    items: ReadonlyArray<ItemType>;
  };
  private readonly elm: HTMLElement;
  private readonly root: Root | null = null;
  private readonly completion: AutoCompletion<ItemType>;
  private readonly itemToNode: (item: CompletionItem) => any;
  private range?: RangeType;
  private query: string;
  private view: any;

  constructor(elm, completion, itemToNode) {
    this.elm = elm;
    this.root = createRoot(this.elm);
    this.completion = completion;
    this.query = "";
    this.state = this.initialState();
    this.itemToNode = itemToNode;
  }

  mount({ view, text, range }: { view: any; text: string; range: RangeType }) {
    this.view = view;
    this.state = this.initialState();

    view.dom.parentNode.appendChild(this.elm);

    this.handleChange({ text, range }).then(() => {
      this.calcPositionOfCompletion();
    });
  }

  unmount() {
    this.elm.remove();
  }

  async handleChange({ text, range }: { text: string; range: RangeType }) {
    this.query = text;
    this.range = range;
    const items = await this.completion.search(text);
    this.state.items = items.slice(0, 20);
    this.state.index = Math.min(this.state.items.length - 1, this.state.index);
    this.render();
  }

  incrementIndex() {
    if (this.state.index + 1 === this.state.items.length) {
      return this.bottomDecrementIndex();
    } else {
      const index = Math.min(this.state.items.length, this.state.index + 1);
      return this.moveIndex(index);
    }
  }

  decrementIndex() {
    if (this.state.index - 1 < 0) {
      return this.topIncrementIndex();
    } else {
      const index = Math.max(-1, this.state.index - 1);
      return this.moveIndex(index);
    }
  }

  topIncrementIndex() {
    const index = this.state.items.length - 1;
    return this.moveIndex(index);
  }

  bottomDecrementIndex() {
    const index = 0;
    return this.moveIndex(index);
  }

  insertCurrentItem() {
    const item = this.currentItem();
    if (item) {
      this.insertItem(item);
      return true;
    } else {
      return false;
    }
  }

  insertItem(item: ItemType) {
    if (this.range) {
      const tr = this.view.state.tr.replaceRangeWith(this.range.from, this.range.to, this.itemToNode(item));
      this.view.dispatch(tr);
    }
  }

  @autobind
  handleClick(item: ItemType) {
    this.insertItem(item);
  }

  private currentItem() {
    const item = this.state.items[this.state.index];
    if (item === this.completion.empty) {
      return null;
    }
    return item;
  }

  private scrollToActiveItem() {
    const container = this.elm;
    const currentItem = container.querySelectorAll("li")[this.state.index];

    const currentItemOffsetBottom = currentItem.offsetTop + currentItem.getBoundingClientRect().height;
    const scrolledContainerHeight = container.scrollTop + container.clientHeight;

    if (currentItemOffsetBottom > scrolledContainerHeight) {
      container.scrollTop = currentItemOffsetBottom - container.clientHeight;
    } else if (currentItem.offsetTop < container.scrollTop) {
      container.scrollTop = currentItem.offsetTop;
    }
  }

  private moveIndex(index: number): boolean {
    if (this.state.items.length === 0) {
      return false;
    }

    this.state.index = index;
    this.scrollToActiveItem();
    this.render();
    return true;
  }

  private render() {
    const hints = (
      <Hints
        items={this.state.items}
        completion={this.completion}
        selectedIndex={this.state.index}
        onClick={this.handleClick}
      />
    );
    this.root?.render(hints);
  }

  private calcPositionOfCompletion() {
    this.elm.removeAttribute("style");
    this.elm.style.maxHeight = `${PopupMaxHeight}em`;

    const documentHeight = document.documentElement.clientHeight;
    const documentWidth = document.documentElement.clientWidth;
    const parentPos = this.elm.offsetParent!.getBoundingClientRect();
    const cursorPos = this.view.coordsAtPos(this.view.state.selection.from);

    const isOverlapEditorHeader = this.isOverlapEditorHeader(cursorPos.top - this.elm.clientHeight);
    if (isOverlapEditorHeader) {
      this.elm.style.top = `${cursorPos.bottom - parentPos.top}px`;
    } else {
      this.elm.style.bottom = `${parentPos.bottom - cursorPos.top}px`;
    }

    const isOverlapRight = cursorPos.right + this.elm.clientWidth > documentWidth;
    if (isOverlapRight) {
      this.elm.style.right = "0px";
    } else {
      this.elm.style.left = `${cursorPos.left - parentPos.left}px`;
    }

    const isOverlapBottom = this.elm.getBoundingClientRect().bottom > documentHeight;
    if (isOverlapBottom) {
      this.elm.style.maxHeight = `${documentHeight - cursorPos.bottom - 8}px`;
    }
  }

  private isOverlapEditorHeader(elementPosTop: number): boolean {
    const editorHeader = document.getElementById("editorHeader");
    if (!editorHeader) {
      return false;
    }

    return elementPosTop < editorHeader.getBoundingClientRect().bottom;
  }

  private initialState() {
    return {
      index: 0,
      items: [],
    };
  }
}

export function completionPlugin<ItemType extends CompletionItem>(
  completion: AutoCompletion<ItemType>,
  itemToNode: (item: ItemType) => any,
  matcher: ($position: any) => any,
) {
  const popupElement = document.createElement("div");
  popupElement.className = "ProseMirror-completion-popup";

  const adapter = new HintsAdapter<ItemType>(popupElement, completion, itemToNode);

  return suggestionsPlugin({
    debug: false,
    matcher,
    onEnter(arg) {
      adapter.mount(arg);
    },
    onChange(arg) {
      adapter.handleChange(arg);
    },
    onExit() {
      adapter.unmount();
    },
    onKeyDown({ event }) {
      let handled = false;
      if (event instanceof KeyboardEvent) {
        switch (event.keyCode) {
          case KEY_UP:
            handled = adapter.decrementIndex();
            break;
          case KEY_DOWN:
            handled = adapter.incrementIndex();
            break;
          case KEY_HOME:
            handled = adapter.bottomDecrementIndex();
            break;
          case KEY_END:
            handled = adapter.topIncrementIndex();
            break;
          case KEY_PAGE_UP:
            handled = adapter.bottomDecrementIndex();
            break;
          case KEY_PAGE_DOWN:
            handled = adapter.topIncrementIndex();
            break;
          case KEY_ESCAPE:
            adapter.unmount();
            handled = true;
            break;
          case KEY_RETURN:
            handled = adapter.insertCurrentItem();
            adapter.unmount();
            break;
          case KEY_TAB:
            handled = adapter.insertCurrentItem();
            adapter.unmount();
            break;
          case KEY_P:
            if (event.ctrlKey) {
              handled = adapter.decrementIndex();
            }
            break;
          case KEY_N:
            if (event.ctrlKey) {
              handled = adapter.incrementIndex();
            }
            break;
        }
      }
      return handled;
    },
  });
}
